import { Activity, Loader2, Scale, UsersRound, Wallet } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchAdminInternalPnL } from '@/api/admin';
import { fmtUsdAmount as fmtUsd } from '@/lib/money';

/**
 * InternalPnLCard — live platform liquidity vs user liability widget.
 *
 * Data source: GET /api/admin/pnl/internal (admin-gated, read-only).
 * Polling cadence: 10 seconds.
 *
 * Two scalar metrics, real-time (no calendar):
 *   - Deposit Balance Pool  — operator cash on hand from user deposits,
 *                             drawn down by officially-paid user payouts.
 *   - Total Withdrawal Liability — user-owed balance, including pending
 *                             withdrawal requests that have not yet been
 *                             officially paid out.
 *
 * Plus a coverage indicator (pool / liability × 100 %) and the absolute
 * surplus or deficit. Coverage 999.99 % is a sentinel meaning "liability
 * is zero, pool is positive" (fully unencumbered).
 */
const COLOR_GOLD = '#FFC20E';
const COLOR_PROFIT = '#10B981';
const COLOR_LOSS = '#EF4444';
const COLOR_CYAN = '#60D7FF';

const InternalPnLCard = () => {
  const [data, setData] = useState(null);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(true);

  const probe = useCallback(async () => {
    try {
      const r = await fetchAdminInternalPnL();
      setData(r);
      setErrored(false);
    } catch {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    probe();
    const t = setInterval(probe, 60000);
    return () => clearInterval(t);
  }, [probe]);

  const pool = data ? parseFloat(data.deposit_pool) : 0;
  const liability = data ? parseFloat(data.user_liability) : 0;
  const surplus = data ? parseFloat(data.surplus) : 0;
  const coverage = data ? parseFloat(data.coverage_pct) : 0;
  const isSurplus = Boolean(data?.is_surplus);
  const isUnencumbered = coverage >= 999;

  const surplusColor = isSurplus ? COLOR_PROFIT : COLOR_LOSS;
  const coverageColor = isUnencumbered ? COLOR_PROFIT : coverage >= 100 ? COLOR_PROFIT : coverage >= 75 ? COLOR_GOLD : COLOR_LOSS;

  // Bar fill % capped at 150 for visualization sanity (avoids 1000%-wide bars).
  const barPct = isUnencumbered ? 100 : Math.max(0, Math.min(150, coverage));

  return (
    <section
      className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden"
      style={{
        border: '1px solid rgba(255, 182, 18, 0.32)',
      }}
      data-testid="admin-internal-pnl-card"
    >
      <div
        className="absolute -top-16 -right-16 w-56 h-56 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(96,215,255,0.16), transparent 60%)',
        }}
        aria-hidden="true"
      />
      {/* Header */}
      <div className="relative z-10 flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0"
            style={{
              background: 'rgba(96,215,255,0.06)',
              borderColor: 'rgba(96,215,255,0.32)',
            }}
            aria-hidden="true"
          >
            <Scale className="w-5 h-5" style={{ color: COLOR_CYAN }} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Internal Liquidity · Real-Time · No Calendar</div>
            <div className="font-display font-bold text-base md:text-lg mt-0.5">Deposit Pool vs User Withdrawal Liability</div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border shrink-0"
          style={{
            color: '#BFE7FF',
            background: 'rgba(96,215,255,0.10)',
            borderColor: 'rgba(96,215,255,0.32)',
          }}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: COLOR_CYAN }} aria-hidden="true" />
          Live
        </span>
      </div>

      {/* Body */}
      {loading && !data ? (
        <div className="relative z-10 flex items-center gap-2 text-[12px] text-[var(--sc-text-mute)] py-6" data-testid="internal-pnl-loading">
          <Loader2 className="w-4 h-4 animate-spin text-[#FFC20E]" />
          Loading liquidity snapshot…
        </div>
      ) : errored && !data ? (
        <div className="relative z-10 text-[12px] text-[var(--sc-text-mute)] py-6" data-testid="internal-pnl-error">
          Snapshot temporarily unavailable. Reconnecting…
        </div>
      ) : data ? (
        <div className="relative z-10">
          {/* Twin tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div
              className="rounded-xl p-4 md:p-5 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255, 182, 18, 0.28)',
              }}
              data-testid="internal-pool-tile"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
                <Wallet className="w-3.5 h-3.5 text-[#FFC20E]" />
                Deposit Balance Pool
              </div>
              <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-1.5 leading-none sc-gold-text" data-testid="internal-pool-value">
                ${fmtUsd(pool)}
              </div>
              <div className="mt-1.5 text-[10px] text-[var(--sc-text-mute)] leading-snug">Lifetime deposits in − Officially paid user withdrawals out</div>
            </div>
            <div
              className="rounded-xl p-4 md:p-5 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(96, 215, 255, 0.28)',
              }}
              data-testid="internal-liability-tile"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
                <UsersRound className="w-3.5 h-3.5" style={{ color: COLOR_CYAN }} />
                Total Withdrawal Liability
              </div>
              <div
                className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-1.5 leading-none"
                style={{ color: COLOR_CYAN }}
                data-testid="internal-liability-value"
              >
                ${fmtUsd(liability)}
              </div>
              <div className="mt-1.5 text-[10px] text-[var(--sc-text-mute)] leading-snug">User withdrawal balances + Pending payout requests</div>
            </div>
          </div>

          {/* Surplus / Coverage row */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
            <div
              className="rounded-xl border p-3 md:p-4"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: `${coverageColor}3D`,
              }}
              data-testid="internal-coverage-bar"
            >
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] mb-2">
                <span className="flex items-center gap-2">
                  <Activity className="w-3 h-3" style={{ color: coverageColor }} />
                  Liability Coverage
                </span>
                <span className="sc-tabular font-semibold" style={{ color: coverageColor }} data-testid="internal-coverage-pct">
                  {isUnencumbered ? '∞' : `${coverage.toFixed(2)}%`}
                </span>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden border"
                style={{
                  background: 'rgba(0,0,0,0.4)',
                  borderColor: `${coverageColor}33`,
                }}
              >
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${(barPct / 150) * 100}%`,
                    background: `linear-gradient(90deg, ${coverageColor}, ${coverageColor}cc)`,
                    boxShadow: `0 0 12px ${coverageColor}88`,
                  }}
                />
              </div>
            </div>
            <div
              className="rounded-xl border p-3 md:p-4 min-w-[180px]"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: `${surplusColor}3D`,
              }}
              data-testid="internal-surplus-tile"
            >
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">{isSurplus ? 'Surplus' : 'Deficit'}</div>
              <div
                className="sc-tabular font-display font-bold text-xl md:text-2xl mt-1 leading-none"
                style={{ color: surplusColor }}
                data-testid="internal-surplus-value"
              >
                {surplus >= 0 ? '+' : '−'}${fmtUsd(Math.abs(surplus))}
              </div>
            </div>
          </div>

          <div className="mt-4 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
            No calendar tracking · Updates with every deposit, earning, or paid-out withdrawal
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default InternalPnLCard;
